import { findEnrollmentReleaseFile } from "@dashboard/downloads/hooks/useProductVersionSelection";
import { ReleaseFilesState } from "@dashboard/downloads/hooks/useReleaseFiles";

const stepParam = "step";
export const codenameParam = "device";
export const versionParam = "version";
const deviceTypeStepKey = "image-selection";

type WizardStep = {
    key: string;
    requiresReleaseFile?: boolean;
};

type WizardStepResolution<Step extends WizardStep> =
    | { kind: "type-selection" }
    | { kind: "loading" }
    | { kind: "redirect"; search: string }
    | { kind: "step"; flow: Step[]; index: number; step: Step; releaseFile: API.ReleaseFile | null };

export const toWizardParams = (stepKey: string | null, releaseFileParams: URLSearchParams) => {
    const params = new URLSearchParams();
    if (!stepKey) {
        return params;
    }
    params.set(stepParam, stepKey);
    const codename = releaseFileParams.get(codenameParam);
    const version = releaseFileParams.get(versionParam);
    if (codename && version) {
        params.set(codenameParam, codename);
        params.set(versionParam, version);
    }
    return params;
};

const findStep = <Step extends WizardStep>(flows: Record<string, Step[]>, stepKey: string) => {
    for (const flow of Object.values(flows)) {
        const index = flow.findIndex((step) => step.key === stepKey);
        if (index !== -1) {
            return { flow, index, step: flow[index] };
        }
    }
    return null;
};

/**
 * Picks what the enrollment wizard shows for the page URL. A URL can be typed, bookmarked or stale, so a step that
 * cannot be shown redirects to the nearest one that can.
 */
export const resolveWizardStep = <Step extends WizardStep>(
    flows: Record<string, Step[]>,
    searchParams: URLSearchParams,
    releaseFiles: ReleaseFilesState,
): WizardStepResolution<Step> => {
    const stepKey = searchParams.get(stepParam);
    if (!stepKey) {
        return { kind: "type-selection" };
    }

    const current = findStep(flows, stepKey);
    if (!current) {
        return { kind: "redirect", search: "" };
    }

    const codename = searchParams.get(codenameParam);
    const version = searchParams.get(versionParam);
    const releaseFile = codename && version ? (findEnrollmentReleaseFile(releaseFiles.data, codename, version) ?? null) : null;

    if (current.step.requiresReleaseFile && !releaseFile) {
        const areReleaseFilesSettled = releaseFiles.status === "fulfilled" || releaseFiles.status === "rejected";
        return areReleaseFilesSettled
            ? { kind: "redirect", search: toWizardParams(deviceTypeStepKey, searchParams).toString() }
            : { kind: "loading" };
    }

    return { kind: "step", ...current, releaseFile };
};
